import React from 'react';
import Cookies from 'js-cookie';

import { Urls } from 'js/src/client';
import { SOCIAL_BUTTON_PROVIDERS } from 'js/src/components/auth/common/SocialButton';
import {
    GOOGLE_ONE_TAP,
    getLastLoginMethod,
    setLastLoginMethod,
} from 'js/src/components/auth/common/lastLoginMethod';

const GSI_CLIENT_SRC = 'https://accounts.google.com/gsi/client';
const CSRF_COOKIE = 'g_csrf_token';

type PromptMoment = {
    isDismissedMoment?: () => boolean;
    isSkippedMoment?: () => boolean;
};

type CredentialResponse = {
    credential?: string;
};

type GoogleAccountsId = {
    initialize: (config: Record<string, unknown>) => void;
    prompt: (listener?: (moment: PromptMoment) => void) => void;
};

/**
 * Only prompt people for whom Google is a plausible way back in. Someone whose last
 * login was Facebook, SSO, or a password should be left alone rather than nudged
 * toward an auth method that may not even reach their account. Google covers both
 * One Tap and the redirect flow — the same identity either way.
 */
export const shouldOfferOneTap = (lastLoginMethod: string | null): boolean =>
    lastLoginMethod === null ||
    lastLoginMethod === GOOGLE_ONE_TAP ||
    lastLoginMethod === SOCIAL_BUTTON_PROVIDERS.GOOGLE;

/**
 * Google One Tap sign-in for logged-out visitors on the marketing site.
 *
 * Google renders the prompt itself and posts a signed ID token to the social auth
 * complete endpoint as a normal form submission, so the browser follows the response
 * and lands the visitor in the app.
 *
 * Under FedCM the prompt is browser UI drawn over the top-right of the viewport: we cannot
 * position it, style it, or reliably tell whether it is on screen. It overlaps the header's
 * own controls while it is up, which is the accepted trade-off for a prompt that is transient
 * and dismissible.
 */
const GoogleOneTap = React.memo(function GoogleOneTap() {
    const clientId = window.SERVER?.GOOGLE_ONETAP_CLIENT_ID;
    const enabled = Boolean(clientId) && shouldOfferOneTap(getLastLoginMethod());

    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        /**
         * One Tap hands the signed credential to a JavaScript callback — ux_mode 'redirect' only
         * posts to login_uri for the Sign in with Google *button*, not for One Tap — so submit it
         * to the social auth complete endpoint ourselves.
         *
         * The backend checks g_csrf_token against a cookie of the same name (the double-submit
         * Google performs on its own POST), so set the cookie alongside the field.
         */
        const submitCredential = (response: CredentialResponse) => {
            if (!response.credential) {
                return;
            }

            setLastLoginMethod(GOOGLE_ONE_TAP);

            const csrfToken = crypto.randomUUID();
            Cookies.set(CSRF_COOKIE, csrfToken, { sameSite: 'lax' });

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = Urls.GOOGLE_ONETAP_AUTH_COMPLETE;
            form.hidden = true;
            for (const [name, value] of [
                ['credential', response.credential],
                [CSRF_COOKIE, csrfToken],
            ]) {
                const field = document.createElement('input');
                field.type = 'hidden';
                field.name = name;
                field.value = value;
                form.appendChild(field);
            }
            document.body.appendChild(form);
            form.submit();
        };

        const startPrompt = () => {
            const accounts = (window as { google?: { accounts?: { id?: GoogleAccountsId } } })
                .google?.accounts?.id;
            if (!accounts) {
                return;
            }

            accounts.initialize({
                client_id: clientId,
                cancel_on_tap_outside: false,
                callback: submitCredential,
            });
            accounts.prompt();
        };

        const script = document.createElement('script');
        script.src = GSI_CLIENT_SRC;
        script.async = true;
        script.nonce = window._CSP_NONCE;
        script.onload = startPrompt;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [enabled, clientId]);

    return null;
});

export default GoogleOneTap;
